import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('meybra_token'));

  useEffect(() => {
    const initAuth = async () => {
      const savedToken = localStorage.getItem('meybra_token');
      if (savedToken) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${savedToken}` }
          });
          let userData = response.data;
          
          // If user is a coach, fetch additional profile info including coach_mode
          if (userData.role === 'coach') {
            try {
              const coachProfile = await axios.get(`${API}/coach/me`, {
                headers: { Authorization: `Bearer ${savedToken}` }
              });
              userData = { ...userData, ...coachProfile.data };
            } catch (e) {
              // Continue with basic data
            }
          }
          
          setUser(userData);
          setToken(savedToken);
        } catch (error) {
          localStorage.removeItem('meybra_token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  // Coach signup
  const coachSignup = async (data) => {
    const response = await axios.post(`${API}/coach/signup`, data);
    const { token: newToken, coach } = response.data;
    localStorage.setItem('meybra_token', newToken);
    setToken(newToken);
    setUser({ ...coach, role: 'coach' });
    return coach;
  };

  // Coach login
  const coachLogin = async (email, password) => {
    const response = await axios.post(`${API}/coach/login`, { email, password });
    const { token: newToken, coach } = response.data;
    localStorage.setItem('meybra_token', newToken);
    setToken(newToken);
    
    // Fetch full profile to get coach_mode
    try {
      const profileResponse = await axios.get(`${API}/coach/me`, {
        headers: { Authorization: `Bearer ${newToken}` }
      });
      const fullProfile = { ...coach, ...profileResponse.data, role: 'coach' };
      setUser(fullProfile);
      return fullProfile;
    } catch (error) {
      // Fallback to basic coach data
      setUser({ ...coach, role: 'coach' });
      return coach;
    }
  };

  // Parent/Athlete login
  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('meybra_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  // Platform admin login
  const platformAdminLogin = async (email, password) => {
    const response = await axios.post(`${API}/platform-admin/login`, { email, password });
    const { token: newToken, admin } = response.data;
    localStorage.setItem('meybra_token', newToken);
    setToken(newToken);
    setUser({ ...admin, role: 'platform_admin' });
    return admin;
  };

  // Register from invite
  const registerFromInvite = async (inviteId, email, password, fullName) => {
    const response = await axios.post(`${API}/auth/register-from-invite`, null, {
      params: { invite_id: inviteId, email, password, full_name: fullName }
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('meybra_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    // Clear ALL role-scoped tokens + cached state. Previously this only
    // cleared meybra_token, which left stale tokens (e.g. club_token,
    // team_token) in localStorage and caused the PWA / unified login
    // auto-redirect to route the user to the wrong role's dashboard.
    try {
      const keysToClear = [
        'meybra_token', 'meybra_user', 'meybra_user_cache',
        'club_token', 'club_info', 'club_user', 'club_admin',
        'team_token', 'team_user', 'team_teams', 'team_coach', 'team_manager',
        'team_coach_token', 'team_coach_user', 'team_coach_teams',
        'platform_admin_token', 'platform_admin_user',
        'coach_token', 'coach_user', 'coach_id',
        'parent_token', 'parent_user',
        'last_login_hint',
      ];
      keysToClear.forEach((k) => localStorage.removeItem(k));
    } catch (e) {
      // graceful — quota / disabled
    }
    setToken(null);
    setUser(null);
  };

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${token}`
  });

  const updateUser = (updates) => {
    console.log('[AuthContext] updateUser called with:', updates);
    setUser(prev => {
      // Deep merge for subscription object to ensure full replacement
      const newUser = { ...prev };
      Object.keys(updates).forEach(key => {
        if (key === 'subscription' && typeof updates[key] === 'object') {
          // Replace subscription entirely, don't merge
          newUser.subscription = { ...updates[key] };
        } else {
          newUser[key] = updates[key];
        }
      });
      console.log('[AuthContext] New user state:', newUser);
      // Also update localStorage to persist the change
      if (newUser && newUser.subscription) {
        localStorage.setItem('meybra_user_cache', JSON.stringify(newUser));
      }
      return newUser;
    });
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      coachSignup,
      coachLogin,
      login,
      platformAdminLogin,
      registerFromInvite,
      logout,
      getAuthHeaders,
      updateUser,
      isCoach: user?.role === 'coach',
      isParent: user?.role === 'parent',
      isPlatformAdmin: user?.role === 'platform_admin'
    }}>
      {children}
    </AuthContext.Provider>
  );
};
